import {
  DOCUMENT,
  Injectable,
  Injector,
  NgZone,
  OnDestroy,
  effect,
  inject,
  untracked,
} from '@angular/core';

import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

const DEFAULT_IDLE_TIMEOUT_MINUTES = 30;
const MS_PER_MINUTE = 60_000;
// setTimeout keeps its delay in a signed 32-bit int; a larger value fires immediately.
const MAX_TIMER_DELAY_MS = 2_147_483_647;
const DASHBOARD_PATH = 'dashboard';

// Any of these counts as the user being active. They are listened for in the capture phase
// because `scroll` doesn't bubble, so scrolling inside a nested overflow container would
// otherwise go unnoticed.
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'click',
  'wheel',
  'scroll',
  'keydown',
  'keyup',
  'touchstart',
  'touchmove',
] as const;

function resolveIdleMs(): number {
  const minutes = Number(environment.idleTimeOutMinutes);
  if (Number.isFinite(minutes) && minutes > 0) return minutes * MS_PER_MINUTE;

  console.warn(
    `[IdleTimeoutService] Invalid idleTimeOutMinutes "${environment.idleTimeOutMinutes}"; ` +
      `falling back to ${DEFAULT_IDLE_TIMEOUT_MINUTES} minutes.`,
  );
  return DEFAULT_IDLE_TIMEOUT_MINUTES * MS_PER_MINUTE;
}

/**
 * Once a signed-in user has been inactive for `environment.idleTimeOutMinutes`, loads `/dashboard`
 * afresh: a full page navigation, so the app reloads from a clean state. Inactivity means none of
 * `ACTIVITY_EVENTS` reached the page.
 *
 * The user is not logged out. Their MSAL session stays in storage and the reload goes through the
 * normal route guards, so they simply land back on the dashboard, still signed in. Watching only
 * runs while a user is authenticated.
 *
 * Activity just stamps `lastActivity` rather than restarting a timer, so a burst of `mousemove`
 * events stays cheap. A single timer wakes up when the window could have elapsed and re-checks.
 */
@Injectable({ providedIn: 'root' })
export class IdleTimeoutService implements OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly ngZone = inject(NgZone);

  private readonly idleMs = resolveIdleMs();

  private lastActivity = 0;
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private watching = false;

  private readonly onActivity = (): void => {
    const now = Date.now();
    // The timer can fire late (laptop asleep, throttled background tab). If the idle window has
    // already elapsed, the first activity afterwards must not quietly extend the session.
    if (now - this.lastActivity >= this.idleMs) {
      this.expire();
      return;
    }
    this.lastActivity = now;
  };

  /** Starts/stops watching as the user signs in and out. Call once at app start-up. */
  init(): void {
    effect(
      () => {
        const authenticated = this.auth.isAuthenticated();
        untracked(() => (authenticated ? this.startWatching() : this.stopWatching()));
      },
      { injector: this.injector },
    );
  }

  ngOnDestroy(): void {
    this.stopWatching();
  }

  private startWatching(): void {
    if (this.watching) return;
    this.watching = true;
    this.lastActivity = Date.now();

    // Outside Angular so the constant stream of mouse/scroll events never triggers change detection.
    this.ngZone.runOutsideAngular(() => {
      for (const type of ACTIVITY_EVENTS) {
        this.document.addEventListener(type, this.onActivity, { capture: true, passive: true });
      }
      this.schedule(this.idleMs);
    });
  }

  private stopWatching(): void {
    if (!this.watching) return;
    this.watching = false;

    for (const type of ACTIVITY_EVENTS) {
      this.document.removeEventListener(type, this.onActivity, { capture: true });
    }
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  private schedule(delayMs: number): void {
    this.timerId = setTimeout(() => this.check(), Math.min(delayMs, MAX_TIMER_DELAY_MS));
  }

  private check(): void {
    this.timerId = null;
    const remainingMs = this.idleMs - (Date.now() - this.lastActivity);
    if (remainingMs <= 0) {
      this.expire();
    } else {
      this.schedule(remainingMs);
    }
  }

  private expire(): void {
    this.stopWatching();
    this.navigate(this.dashboardUrl());
  }

  // Resolved against <base href> so it stays correct if the app is ever served from a sub-path.
  private dashboardUrl(): string {
    return new URL(DASHBOARD_PATH, this.document.baseURI).href;
  }

  /** Full navigation (not a router navigation), so the app reloads even if already on /dashboard. */
  private navigate(url: string): void {
    this.document.location.assign(url);
  }
}
